<script>
    import { onMount } from 'svelte';
    import { page } from '$app/stores';
    import Card from './Card.svelte';
    import PurchasePaddle from './PurchasePaddle.svelte';
    import { img_cdn } from './utils/image';
    import { logEvent } from './utils/logs';
    import { discount_data } from './stores/globals';
    import { patch_single_product_prices } from './utils/patch_localized_prices';
    import { apply_discount, discount_applies } from './utils/discount';
    import H3 from './H3.svelte';

    let className = '';
    export { className as class };

    /** The product page this strip upsells from. */
    /** @type {Course | Worksheet} */
    export let product;

    export let feature = 'upsell-strip';

    // Bumped after localized prices land; forces the derived chain to re-read
    // the in-place patched bundle objects.
    let price_version = 0;

    $: candidates =
        price_version >= 0
            ? ($page.data.bundles || []).filter(
                  (bundle) =>
                      bundle.listed &&
                      bundle.items.some(
                          (item) =>
                              item.type === product.type &&
                              item.slug === product.slug
                      )
              )
            : [];

    // The product's priority bundle goes first; any bundle with an
    // already-owned item is skipped (it would double-charge for that part).
    $: prioritized = product.priority_bundle
        ? [
              ...candidates.filter(
                  (bundle) => bundle.text_id === product.priority_bundle
              ),
              ...candidates.filter(
                  (bundle) => bundle.text_id !== product.priority_bundle
              )
          ]
        : candidates;

    $: bundle =
        prioritized.find(
            (candidate) => !candidate.items.some((item) => item.purchased)
        ) || null;

    /** @param {Course | Worksheet | Extra} item */
    const is_self = (item) =>
        item.type === product.type && item.slug === product.slug;

    $: thumb_items = bundle
        ? [
              ...bundle.items.filter(is_self),
              ...bundle.items.filter((item) => !is_self(item))
          ]
        : [];

    $: addon_line = bundle
        ? bundle.items
              .filter((item) => !is_self(item))
              .map((item) => `+ ${item.short_title || item.title}`)
              .join(' ')
        : '';

    /** Non-breaking space after each "+" so it stays glued to the word when the line wraps. */
    /** @param {string} line */
    const glue_plus = (line) => line.replace(/\+ /g, '+\u00A0');

    /** Same discount math as PurchasePaddle, so the savings % matches the button prices. */
    /** @param {Price} price_data @param {DiscountData} discount */
    const effective_price = (price_data, discount) => {
        const price = price_data?.price || 0;
        return discount_applies(discount, price_data)
            ? apply_discount(price, discount)
            : price;
    };

    $: bundle_effective = bundle
        ? effective_price(bundle.price, $discount_data)
        : 0;
    $: list_price = bundle
        ? Math.max(bundle.price_items?.price || 0, bundle.price?.price || 0)
        : 0;
    $: save_percent =
        list_price > 0
            ? Math.round(((list_price - bundle_effective) / list_price) * 100)
            : 0;

    const track_bundle_link = () => {
        logEvent('upsell-strip-bundle-link', {
            product_slug: product.slug,
            product_type: product.type,
            bundle: bundle?.slug,
            feature
        }); 
    };

    onMount(() => {
        // Localize displayed prices (resolves from cache once the layout's
        // global pricing request lands).
        Promise.all(candidates.map(patch_single_product_prices)).then(() => {
            price_version += 1;
        });
    });
</script>

{#if bundle && !product.purchased && product.price}
    <div class="@container {className}">
        <Card
            p_sm
            class="flex w-full flex-col items-start gap-4 @2xl:w-fit @xl:flex-row @xl:flex-wrap @xl:items-center @2xl:flex-nowrap @2xl:gap-6"
        >
            <div
                class="@max-xl:hidden flex shrink-0 flex-wrap items-center justify-center pt-4 @2xl:max-w-32"
                aria-hidden="true"
            >
                {#each thumb_items as item}
                    <img
                        src={img_cdn(item.icon, {
                            w: 96,
                            h: 96,
                            fit: 'contain'
                        })}
                        alt=""
                        width="48"
                        height="48"
                        loading="lazy"
                        class="-ml-4 -mt-4 size-12 rounded-full border-2 border-black bg-black object-cover first:ml-0"
                    />
                {/each}
            </div>
            <div class="min-w-0 space-y-1">
                <h3 class="font-bold leading-tight">
                    {#if save_percent > 0 && save_percent < 100}
                        <span class="text-symbols-emphasis"
                            >Save {save_percent}%</span
                        > in the
                    {:else}
                        Get more in the
                    {/if}
                    <a
                        class="link"
                        href="/bundles/{bundle.slug}"
                        on:click={track_bundle_link}
                        >{bundle.short_title || bundle.title}</a
                    >
                </h3>
                <p class="text-sm leading-tight">
                    {glue_plus(addon_line)}
                </p>
            </div>
            
            <PurchasePaddle
                product={bundle}
                cta="Buy Bundle"
                cta_free="Unlock for free"
                style="small"
                {feature}
                class="basis-full shrink-0 @2xl:basis-0"
            />
            
        </Card>
    </div>
{/if}
